import { Button, Label } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { Camera, Check, ChevronLeft, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { submitVendorSignoff, vendorPresignUpload } from '../../api/vendor-work-orders.js';
import { SignaturePad } from '../../components/signature-pad.js';

type Step = 'photos' | 'notes' | 'signature' | 'confirm';

async function uploadCompressedPhoto(file: File): Promise<string> {
  // Force JPEG: iOS cameras produce HEIC/HEIF, which isn't an allowed
  // `work_order_photo` content type and would 400 at presign.
  const compressed = await imageCompression(file, {
    maxWidthOrHeight: 1920,
    initialQuality: 0.85,
    fileType: 'image/jpeg',
  });
  const jpegName = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
  const presign = await vendorPresignUpload({
    kind: 'work_order_photo',
    contentType: 'image/jpeg',
    filename: jpegName,
    sizeBytes: compressed.size,
  });
  await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: presign.headers,
    body: compressed,
  });
  return presign.uploadId;
}

async function uploadSignaturePng(blob: Blob): Promise<string> {
  const presign = await vendorPresignUpload({
    kind: 'work_order_signature',
    contentType: 'image/png',
    filename: 'signature.png',
    sizeBytes: blob.size,
  });
  await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: presign.headers,
    body: blob,
  });
  return presign.uploadId;
}

export function VendorSignoffFlowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('photos');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<Array<{ id: string; uploadId?: string; previewUrl: string }>>(
    [],
  );
  const [signatureUploadId, setSignatureUploadId] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Missing work order id');
      const uploadIds = photos.map((p) => p.uploadId).filter((v): v is string => Boolean(v));
      if (uploadIds.length === 0) throw new Error('Add at least one photo');
      if (!signatureUploadId) throw new Error('Signature is required');
      await submitVendorSignoff(id, {
        completionNotes: notes.trim() || null,
        photoUploadIds: uploadIds,
        signatureUploadId,
      });
    },
    onSuccess: () => navigate(`/vendor/work/${id}`, { replace: true }),
    onError: (e: Error) => setError(e.message),
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const local = {
          id: crypto.randomUUID(),
          previewUrl: URL.createObjectURL(file),
        };
        setPhotos((prev) => [...prev, local]);
        try {
          const uploadId = await uploadCompressedPhoto(file);
          setPhotos((prev) => prev.map((p) => (p.id === local.id ? { ...p, uploadId } : p)));
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Upload failed');
          setPhotos((prev) => prev.filter((p) => p.id !== local.id));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSignature = async (blob: Blob) => {
    setError(null);
    setUploading(true);
    try {
      const uploadId = await uploadSignaturePng(blob);
      setSignatureUploadId(uploadId);
      setSignaturePreview(URL.createObjectURL(blob));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signature upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-8">
      <Link
        to={`/vendor/work/${id}`}
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-fg"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </Link>

      <StepHeader step={step} />

      {error ? (
        <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {step === 'photos' ? (
        <section className="space-y-3">
          <Label>Photos (at least one is required)</Label>
          <div className="flex flex-wrap gap-2">
            {photos.map((p) => (
              <div
                key={p.id}
                className="relative h-24 w-24 overflow-hidden rounded-md border border-border"
              >
                <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setPhotos((prev) => prev.filter((x) => x.id !== p.id))}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"
                  aria-label="Remove photo"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                {!p.uploadId ? (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] text-white">
                    Uploading…
                  </div>
                ) : null}
              </div>
            ))}
            <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-muted">
              <Camera className="h-6 w-6" />
              <span className="text-xs">Add photo</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="sr-only"
                onChange={(e) => {
                  void handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <NavRow
            backLabel="Cancel"
            onBack={() => navigate(`/vendor/work/${id}`)}
            nextLabel="Next"
            nextDisabled={
              uploading || photos.length === 0 || photos.some((p) => !p.uploadId)
            }
            onNext={() => setStep('notes')}
          />
        </section>
      ) : null}

      {step === 'notes' ? (
        <section className="space-y-3">
          <Label htmlFor="notes">What was done</Label>
          <textarea
            id="notes"
            rows={5}
            className="min-h-32 w-full rounded-md border border-border bg-surface p-3 text-base"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Short summary of the work completed."
          />
          <NavRow
            backLabel="Back"
            onBack={() => setStep('photos')}
            nextLabel="Next"
            onNext={() => setStep('signature')}
          />
        </section>
      ) : null}

      {step === 'signature' ? (
        <section className="space-y-3">
          <Label>Signature (required)</Label>
          {signaturePreview ? (
            <div className="space-y-2">
              <img
                src={signaturePreview}
                alt="Captured signature"
                className="rounded-md border border-border bg-surface p-2"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSignaturePreview(null);
                  setSignatureUploadId(null);
                }}
              >
                Sign again
              </Button>
            </div>
          ) : (
            <SignaturePad
              onAccept={(blob) => void handleSignature(blob)}
              uploaded={Boolean(signatureUploadId)}
            />
          )}
          <NavRow
            backLabel="Back"
            onBack={() => setStep('notes')}
            nextLabel="Next"
            nextDisabled={!signatureUploadId}
            onNext={() => setStep('confirm')}
          />
        </section>
      ) : null}

      {step === 'confirm' ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-fg">Review and submit</h2>
          <dl className="space-y-2 rounded-md border border-border bg-surface p-4 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted">Photos</dt>
              <dd className="text-fg">{photos.length}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted">Signature</dt>
              <dd className="text-fg">{signatureUploadId ? 'Captured' : 'Missing'}</dd>
            </div>
            <div>
              <dt className="text-muted">Notes</dt>
              <dd className="mt-1 whitespace-pre-wrap text-fg">{notes.trim() || '—'}</dd>
            </div>
          </dl>
          <NavRow
            backLabel="Back"
            onBack={() => setStep('signature')}
            nextLabel={submitMutation.isPending ? 'Submitting…' : 'Submit sign-off'}
            nextIcon={<Check className="h-4 w-4" />}
            nextDisabled={submitMutation.isPending || !signatureUploadId || photos.length === 0}
            onNext={() => submitMutation.mutate()}
          />
        </section>
      ) : null}
    </div>
  );
}

function StepHeader({ step }: { step: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: 'photos', label: 'Photos' },
    { id: 'notes', label: 'Notes' },
    { id: 'signature', label: 'Signature' },
    { id: 'confirm', label: 'Confirm' },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div>
      <h1 className="text-lg font-semibold text-fg">Sign off work</h1>
      <ol className="mt-2 flex gap-2 text-xs text-muted">
        {steps.map((s, i) => (
          <li key={s.id} className={i === idx ? 'font-medium text-fg' : ''}>
            {i + 1}. {s.label}
            {i < steps.length - 1 ? <span className="ml-2 text-border">›</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function NavRow({
  backLabel,
  onBack,
  nextLabel,
  onNext,
  nextDisabled,
  nextIcon,
}: {
  backLabel: string;
  onBack: () => void;
  nextLabel: string;
  onNext: () => void;
  nextDisabled?: boolean;
  nextIcon?: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 pt-2">
      <Button type="button" variant="secondary" className="min-h-11 flex-1" onClick={onBack}>
        {backLabel}
      </Button>
      <Button
        type="button"
        className="min-h-11 flex-1"
        onClick={onNext}
        disabled={nextDisabled}
      >
        {nextIcon}
        {nextLabel}
      </Button>
    </div>
  );
}
